/**
 * The organisation's "how many days a password lasts" setting, as authentication/org-details reports
 * it and authentication/edit-org accepts it. Empty means the platform default.
 */

export interface PasswordExpiryInfo {
  password_expiry_days?: number;
  password_expiry_default_days?: number;
  password_expiry_min_days?: number;
  password_expiry_max_days?: number;
  can_edit_password_expiry?: boolean;
}

/** Helper text under the field. */
export const passwordExpiryHint = (info: PasswordExpiryInfo): string =>
  `Between ${info.password_expiry_min_days} and ${info.password_expiry_max_days} days. ` +
  `Leave empty for the default (${info.password_expiry_default_days}). ` +
  "Applies to everyone in the organization, counted from each person's last password change.";

/**
 * The field as text. It is a number input, and Formik stores what is typed there as a number (or ""
 * when it is cleared), not a string. Calling .trim() on that threw inside Formik's validation, which
 * left the form stuck on "Submitting..." without sending the request.
 */
const fieldText = (value: string | number | null | undefined): string => String(value ?? "").trim();

/** Error text for the days field, or "" when it can be saved. */
export const passwordExpiryError = (value: string | number, info: PasswordExpiryInfo): string => {
  const text = fieldText(value);
  if (text === "") return "";
  const min = info.password_expiry_min_days ?? 0;
  const max = info.password_expiry_max_days ?? 0;
  if (!/^\d+$/.test(text)) return "Enter a whole number of days.";
  const days = Number(text);
  return days < min || days > max ? `Enter between ${min} and ${max} days.` : "";
};

/**
 * What to add to the edit-org request: nothing when the setting is untouched (so saving the address
 * never pins the setting), null when the field was emptied (back to the default), else the number.
 */
export const passwordExpiryPayload = (
  value: string | number,
  info: PasswordExpiryInfo
): { password_expiry_days?: number | null } => {
  if (!info.can_edit_password_expiry) return {};
  const text = fieldText(value);
  if (text === String(info.password_expiry_days)) return {};
  return { password_expiry_days: text === "" ? null : Number(text) };
};
